/**
 * Every real page on the site. Section pages reuse the homepage section component, so a visitor on the
 * homepage still scrolls through everything, while Google sees a distinct page (own URL, title, H1,
 * description and canonical) for each part of the business. That set of pages is what sitelinks are built from.
 */
export const SITE = 'https://www.ivyleaguepainting.com';

export type SectionPageConfig = {
  /** id of the section on the homepage */
  id: string;
  /** real URL of the standalone page */
  to: string;
  /** short label used in navigation */
  text: string;
  eyebrow: string;
  h1: string;
  intro: string;
  title: string;
  description: string;
};

export const sectionPages: SectionPageConfig[] = [
  {
    id: 'services',
    to: '/services',
    text: 'Services',
    eyebrow: 'Our services',
    h1: 'Painting Services in Delta, BC',
    intro:
      'Interior and exterior painting, kitchen cabinets, strata, commercial and warehouse work across Delta and the Lower Mainland, all with a written quote, $5M insurance and a 3-year guarantee.',
    title: 'Painting Services in Delta, BC | Interior, Exterior, Cabinets & Strata | Ivy League Painting',
    description:
      'Interior, exterior, kitchen cabinet, strata, commercial and warehouse painting in Delta and the Lower Mainland. $5M insured, BBB A+, 3-year guarantee. Free on-site quotes.',
  },
  {
    id: 'work',
    to: '/work',
    text: 'Work',
    eyebrow: 'Our work',
    h1: 'Our Painting Projects',
    intro:
      'Real homes and buildings we have painted around Delta, Ladner, Tsawwassen, Surrey and Vancouver, and the four-step process behind every one.',
    title: 'Our Work | Painting Projects in Delta & the Lower Mainland | Ivy League Painting',
    description:
      'See interior, exterior and cabinet painting projects by Ivy League Painting in Delta, Ladner, Tsawwassen, Surrey and Vancouver, and how our four-step process works.',
  },
  {
    id: 'reviews',
    to: '/reviews',
    text: 'Reviews',
    eyebrow: 'Reviews',
    h1: 'Ivy League Painting Reviews',
    intro:
      'What homeowners and property managers across Delta and the Lower Mainland say about working with Jack and the crew.',
    title: 'Reviews | 5.0-Rated Painters in Delta, BC | Ivy League Painting',
    description:
      'Read all 29 Google reviews of Ivy League Painting from homeowners in Delta, Tsawwassen and the Lower Mainland. 5.0 average rating, BBB A+ accredited, 3-year guarantee.',
  },
  {
    id: 'about',
    to: '/about',
    text: 'About',
    eyebrow: 'About us',
    h1: 'About Ivy League Painting',
    intro:
      'A full-service painting company founded in Delta, BC. Over 10 years and 425+ customers later, we still paint every job like it is our own home.',
    title: 'About Us | Ivy League Painting, Delta BC Painting Company',
    description:
      'Ivy League Painting is a Delta, BC painting company with 10+ years of experience, 425+ happy customers, $5M liability insurance and a 3-year guarantee.',
  },
  {
    id: 'areas',
    to: '/areas',
    text: 'Areas',
    eyebrow: 'Service areas',
    h1: 'Areas We Serve',
    intro:
      'Based in Delta, we paint homes, strata buildings and businesses in Ladner, Tsawwassen, North Delta, Surrey, White Rock, Richmond, Vancouver and across the Lower Mainland.',
    title: 'Service Areas | Painters in Delta, Surrey, Richmond & the Lower Mainland | Ivy League Painting',
    description:
      'Ivy League Painting serves Ladner, Tsawwassen, North Delta, Surrey, White Rock, Richmond, Vancouver, Burnaby and more. Free on-site painting quotes.',
  },
  {
    id: 'faq',
    to: '/faq',
    text: 'FAQ',
    eyebrow: 'FAQ',
    h1: 'Painting Questions, Answered',
    intro: 'Estimates, insurance, timelines, paint and guarantees: the questions we hear most, with straight answers.',
    title: 'Painting FAQ | Estimates, Timelines & Guarantees | Ivy League Painting',
    description:
      'Answers to common painting questions: free estimates, insurance, how long projects take, whether you need to buy paint, and our 3-year guarantee.',
  },
  {
    id: 'delta',
    to: '/community',
    text: 'Community',
    eyebrow: 'Community',
    h1: 'Giving Back to Delta',
    intro:
      'We are founded and operated in Delta, so we support the community that supports us, from local sports to KidSport and the Ladner May Days parade.',
    title: 'Community | Ivy League Painting in Delta, BC',
    description:
      'Ivy League Painting sponsors local lacrosse players, contributes to KidSport and has joined the Ladner May Days parade since 2017.',
  },
  {
    id: 'contact',
    to: '/contact',
    text: 'Contact',
    eyebrow: 'Contact',
    h1: 'Get a Free Painting Quote',
    intro:
      'Tell us about your project and we will follow up within one business day. Prefer to talk? Call (604) 365-5225 or email ivyleaguepaint@gmail.com.',
    title: 'Contact Us | Free Painting Quote in Delta, BC | Ivy League Painting',
    description:
      'Request a free, no-obligation painting quote from Ivy League Painting in Delta, BC. Call (604) 365-5225 or send your project details online.',
  },
];

/** Standalone location pages that have no homepage section. */
export const locationPages = [{ to: '/painters-delta', text: 'Delta' }];

/** Every pre-rendered, indexable route. Used by the sitemap and the pre-render step. */
export const allRoutes = ['/', ...sectionPages.map((p) => p.to), ...locationPages.map((p) => p.to)];
